/**
 * **口径对账卡**（批 `99` B3 · `AC-INV9` / `AC-INV4` 的报表侧）
 *
 * ## 这张卡回答的问题
 *
 * 「投资页说我赚了 ¥804.85，报表里为什么看不到？」—— 对账把两条路径的数并排放：
 *
 * | 侧 | 来源 |
 * |---|---|
 * | 报表 | `legs` 按分类聚合（`cat-invest-gain` / `cat-invest`） |
 * | 投资记录 | `investment_transactions.realized_pnl` |
 *
 * ## 🔴 三条展示纪律
 *
 * 1. **不可比就说不可比**（外币 / 有外币流水）—— 不给一个假的对上；
 * 2. **差额必须配解释**（`reconcileNoteOf`）：历史数据没进报表 / 分类被 R 域共用 / 请人工核对；
 * 3. **数字全部取接口字段**，端侧只做展示与"读差值的意义"，不重算任何金额。
 */

import type { ReactElement } from 'react';
import { Alert, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { toCents, type InvestmentReconcileView } from '@jiaying/shared';
import { reconcileNoteOf, reconcileVerdictOf } from '../features/investment.js';
import {
  RECONCILE_DOMAIN_SIDE,
  RECONCILE_EMPTY,
  RECONCILE_FX_INCOMPLETE,
  RECONCILE_GAIN,
  RECONCILE_HINT,
  RECONCILE_LOSS,
  RECONCILE_REPORT_SIDE,
  RECONCILE_TITLE,
} from '../features/investment-labels.js';
import { Amount } from './Amount.js';

export function InvestmentReconcileCard({
  reconcile,
  noticeColor,
}: {
  reconcile: InvestmentReconcileView;
  /** 琥珀色（`theme.jiaying.tokens['c-notice']`）—— 与年化样本提示同一个色 */
  noticeColor: string;
}): ReactElement {
  const hasAny = reconcile.items.some(
    (r) => r.reportGainCents + r.reportLossCents + r.domainGainCents + r.domainLossCents > 0,
  );

  return (
    <Card data-testid="invest-reconcile">
      <CardContent sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {RECONCILE_TITLE}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            {RECONCILE_HINT}
          </Typography>
          <Typography variant="caption" color="text.secondary" data-testid="reconcile-window">
            {reconcile.window.from} → {reconcile.window.to}
          </Typography>
        </Stack>

        {reconcile.fxIncomplete && (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="reconcile-fx-incomplete">
            {RECONCILE_FX_INCOMPLETE}
          </Alert>
        )}

        {!hasAny && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {RECONCILE_EMPTY}
          </Typography>
        )}

        <Stack spacing={1.25} sx={{ mt: 1.25 }}>
          {reconcile.items.map((row) => {
            const note = reconcileNoteOf(row);
            return (
              <Box key={row.currency} data-testid={`reconcile-row-${row.currency}`}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <Chip size="small" variant="outlined" label={row.currency} />

                  {/* 收益侧：报表 vs 投资记录（两个数并排，不合并） */}
                  <SidePair
                    label={RECONCILE_GAIN}
                    currency={row.currency}
                    reportCents={row.reportGainCents}
                    domainCents={row.domainGainCents}
                    testId={`reconcile-gain-${row.currency}`}
                  />
                  {/* 亏损侧 */}
                  <SidePair
                    label={RECONCILE_LOSS}
                    currency={row.currency}
                    reportCents={row.reportLossCents}
                    domainCents={row.domainLossCents}
                    testId={`reconcile-loss-${row.currency}`}
                  />

                  <Box sx={{ flexGrow: 1 }} />
                  <Chip
                    size="small"
                    label={reconcileVerdictOf(row)}
                    color={note.attention ? 'default' : 'success'}
                    variant={note.attention ? 'outlined' : 'filled'}
                    sx={
                      note.attention && note.kind !== 'notComparable'
                        ? { color: noticeColor, borderColor: noticeColor }
                        : undefined
                    }
                    data-testid={`reconcile-verdict-${row.currency}`}
                  />
                </Stack>

                {/* 差额与解释：只有真的不等（或不可比）时才出现 */}
                {(note.kind === 'history' || note.kind === 'shared' || note.kind === 'unknown') && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.25, color: noticeColor }}
                    data-testid={`reconcile-note-${row.currency}`}
                  >
                    {note.text}
                  </Typography>
                )}
                {note.kind === 'notComparable' && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25 }}
                    data-testid={`reconcile-note-${row.currency}`}
                  >
                    {note.text}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

/** 「收益 / 亏损」的一组：`报表 X · 投资记录 Y`（两个数分别带侧名，避免被读成"合计"） */
function SidePair({
  label,
  currency,
  reportCents,
  domainCents,
  testId,
}: {
  label: string;
  currency: string;
  reportCents: number;
  domainCents: number;
  testId: string;
}): ReactElement {
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline" data-testid={testId}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {RECONCILE_REPORT_SIDE}
      </Typography>
      <Amount cents={toCents(reportCents)} currency={currency} variant="caption" colored={false} />
      <Typography variant="caption" color="text.secondary">
        {RECONCILE_DOMAIN_SIDE}
      </Typography>
      <Amount cents={toCents(domainCents)} currency={currency} variant="caption" colored={false} />
    </Stack>
  );
}
